import {
  COVERAGE_WARN,
  cashOut,
  fmt,
  fmtK,
  formatByUnit,
  presetRange,
  profitAndLoss,
  todayKey,
  topSellers,
} from '@k2pos/core';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HazardStripes } from '@/components/brand/HazardStripes';
import { AppBar } from '@/components/ui/AppBar';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { CloseDaySheet } from '@/components/ui/CloseDaySheet';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { Mono, Text } from '@/components/ui/Text';
import { useBackHandling, useGoBack } from '@/hooks/useNav';
import { useMoneyAction } from '@/hooks/useMoneyAction';
import { usePos } from '@/store/pos';
import { colors } from '@/theme/tokens';
import styles from './Report.module.css';

/** ປິດຍອດ · ລາຍງານ — the end-of-day close. */
export function Report() {
  const navigate = useNavigate();
  const goBack = useGoBack('/more');
  const salesToday = usePos((s) => s.salesToday);
  const receivedToday = usePos((s) => s.receivedToday);
  const billsToday = usePos((s) => s.billsToday);
  const collectedToday = usePos((s) => s.collectedToday);
  const journal = usePos((s) => s.journal);
  const closeDay = usePos((s) => s.closeDay);
  const [sheetOpen, setSheetOpen] = useState(false);

  const commit = useCallback((counted: number | null) => closeDay(counted), [closeDay]);
  const { run, busy, error, dismiss } = useMoneyAction(commit, () => {
    setSheetOpen(false);
    navigate('/', { replace: true });
  });

  // Android back closes the sheet rather than leaving the screen behind it.
  useBackHandling(false, sheetOpen ? 1 : 0, () => setSheetOpen(false));

  const credited = Math.max(0, salesToday - receivedToday);
  const paidFraction = salesToday > 0 ? receivedToday / salesToday : 0;

  // Real figures from the ledger. These were hard-coded constants — a fixed
  // 540,000 "profit" and three invented best-sellers — which looked plausible
  // and were never anything but decoration.
  const today = presetRange('today', todayKey());
  const pl = useMemo(() => profitAndLoss(journal, today), [journal, today]);
  const sellers = useMemo(() => topSellers(journal, today, 3), [journal, today]);
  // The strip is only honest if the day's sales were mostly costed.
  const hasCosts = pl.coverage > 0;
  // Money that should physically be in the drawer: cash in, less cash out.
  // Cash expenses and cash purchases leave the till, so without subtracting them
  // this figure could never balance against the count the owner does at night.
  const paidOut = cashOut(journal, presetRange('today', todayKey()));
  const drawer = receivedToday + collectedToday - paidOut;

  return (
    <>
      <AppBar title="ປິດຍອດ · ລາຍງານ" onBack={goBack} />

      <div className={`${styles.body} vscroll`}>
        <ErrorBanner message={error} onDismiss={dismiss} />

        <div className={styles.total}>
          <HazardStripes className={styles.stripes} opacity={0.35} period={18} />
          <Text size={12} color={colors.onDarkBright}>
            ຍອດຂາຍມື້ນີ້
          </Text>
          <Mono weight={700} size={28} color={colors.sky} className={`${styles.amount} selectable`}>
            {fmt(salesToday)}
          </Mono>
          <Text size={11} color={colors.onDarkSoft}>
            ₭ · ລວມທັງຂາຍຕິດ
          </Text>

          <div className={styles.splitTrack}>
            <div className={styles.splitPaid} style={{ flexGrow: Math.max(0.001, paidFraction) }} />
            <div
              className={styles.splitCredit}
              style={{ flexGrow: Math.max(0.001, 1 - paidFraction) }}
            />
          </div>

          <div className={styles.legend}>
            <LegendItem color={colors.pageBg} label="ຈ່າຍເລີຍ" value={fmtK(receivedToday)} />
            <LegendItem color={colors.sky} label="ຕິດໜີ້" value={fmtK(credited)} />
          </div>
        </div>

        {hasCosts ? (
          <div className={styles.profit}>
            <div className="grow col">
              <Text weight={800} size={12} color={colors.skyDeep}>
                ກຳໄລມື້ນີ້ · ຂາຍ − ຕົ້ນທຶນ
              </Text>
              <Text size={9.5} color={colors.textMuted}>
                {pl.coverage < COVERAGE_WARN
                  ? `ນັບຕົ້ນທຶນໄດ້ ${Math.round(pl.coverage * 100)}% ຂອງຍອດຂາຍ`
                  : 'ນັບສະເພາະລາຍການທີ່ໃສ່ຕົ້ນທຶນໄວ້'}
              </Text>
            </div>
            <Mono weight={700} size={16} color={colors.skyDeep}>
              {pl.grossProfit >= 0 ? '+' : ''}
              {fmt(pl.grossProfit)} ₭
            </Mono>
          </div>
        ) : null}

        <div className={styles.tiles}>
          <Card className={styles.tile}>
            <Text size={10.5} color={colors.textMuted}>
              ຈຳນວນບິນ
            </Text>
            <Mono weight={700} size={18}>
              {billsToday}
            </Mono>
          </Card>
          <Card className={styles.tile}>
            <Text size={10.5} color={colors.textMuted}>
              ຮັບຊຳລະໜີ້
            </Text>
            <Mono weight={700} size={18} color={colors.skyDeep}>
              {fmtK(collectedToday)}
            </Mono>
          </Card>
        </div>

        <Text weight={800} size={11} color={colors.textMuted} className={styles.label}>
          ຂາຍດີສຸດ
        </Text>
        {sellers.map((item, i) => (
          <Card key={item.productId} className={styles.sellerRow}>
            <Mono weight={700} size={12} color={colors.textFaint} className={styles.rank}>
              {i + 1}
            </Mono>
            <div className="grow col">
              <Text weight={700} size={13} lines={1}>
                {item.name}
              </Text>
              {/* Per unit, never summed: 48 ຖົງ plus 0.5 ໂຕນ is not 48.5. */}
              <Mono size={10.5} color={colors.textMuted}>
                {formatByUnit(item.byUnit)}
              </Mono>
            </div>
            <Mono weight={700} size={13}>
              {fmtK(item.amount)}
            </Mono>
          </Card>
        ))}

        {sellers.length === 0 ? (
          <Text size={11.5} color={colors.textFaint} align="center">
            ຍັງບໍ່ມີການຂາຍມື້ນີ້
          </Text>
        ) : null}

        <Card className={styles.drawer}>
          <div className="grow col">
            <Text size={11.5} color={colors.textMuted}>
              ເງິນສົດຄວນມີໃນລິ້ນຊັກ
            </Text>
            {paidOut > 0 ? (
              <Mono size={10.5} color={colors.textFaint}>
                ຫັກຈ່າຍດ້ວຍເງິນສົດແລ້ວ {fmt(paidOut)} ₭
              </Mono>
            ) : null}
          </div>
          <Mono weight={700} size={15} className="selectable">
            {fmt(drawer)} ₭
          </Mono>
        </Card>

        <Button label="ນັບເງິນ ແລະ ປິດຍອດ" onPress={() => setSheetOpen(true)} />
      </div>

      <CloseDaySheet
        open={sheetOpen}
        expected={drawer}
        busy={busy}
        onConfirm={run}
        onClose={() => setSheetOpen(false)}
      />
    </>
  );
}

function LegendItem({ color, label, value }: { color: string; label: string; value: string }) {
  return (
    <div className={styles.legendItem}>
      <span className={styles.swatch} style={{ background: color }} />
      <Text size={10.5} color={colors.onDarkSoft}>
        {label}
      </Text>
      <Mono weight={700} size={10.5} color="#FFFFFF">
        {value}
      </Mono>
    </div>
  );
}
